/**
 * useBrandSettings Hook
 * =====================
 * Phase 6: Single source of truth for brand settings
 * Fetches from Admin Company Settings and provides to all Marketing Hub components
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default brand settings (fallback)
const DEFAULT_BRAND_SETTINGS = {
  company_name: 'RhinoShield Florida',
  logo_url: null,
  logo_dark_url: null,
  primary_color: '#2563eb',
  secondary_color: '#1e40af',
  brand_colors: [
    '#1E3A8A', '#3B82F6', '#EAB308', '#000000', '#FFFFFF',
    '#22C55E', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4'
  ],
  default_font: 'Inter',
  marketing_channels: ['facebook', 'instagram', 'linkedin', 'twitter', 'blog']
};

// Cache for brand settings to avoid redundant fetches
let cachedSettings = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to fetch and cache brand settings from Admin Company Settings
 * @returns {Object} { brandSettings, loading, error, refetch }
 */
export const useBrandSettings = () => {
  const [brandSettings, setBrandSettings] = useState(cachedSettings || DEFAULT_BRAND_SETTINGS);
  const [loading, setLoading] = useState(!cachedSettings);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async (force = false) => {
    // Use cache if valid and not forcing refresh
    const now = Date.now();
    if (!force && cachedSettings && (now - cacheTimestamp) < CACHE_TTL) {
      setBrandSettings(cachedSettings);
      setLoading(false);
      return cachedSettings;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/settings/company`, { headers });
      
      const settings = {
        company_name: response.data.company_name || DEFAULT_BRAND_SETTINGS.company_name,
        logo_url: response.data.logo_url || DEFAULT_BRAND_SETTINGS.logo_url,
        logo_dark_url: response.data.logo_dark_url || DEFAULT_BRAND_SETTINGS.logo_dark_url,
        primary_color: response.data.primary_color || DEFAULT_BRAND_SETTINGS.primary_color,
        secondary_color: response.data.secondary_color || DEFAULT_BRAND_SETTINGS.secondary_color,
        brand_colors: response.data.brand_colors || DEFAULT_BRAND_SETTINGS.brand_colors,
        default_font: response.data.default_font || DEFAULT_BRAND_SETTINGS.default_font,
        marketing_channels: response.data.marketing_channels || DEFAULT_BRAND_SETTINGS.marketing_channels
      };
      
      // Update cache
      cachedSettings = settings;
      cacheTimestamp = now;
      
      setBrandSettings(settings);
      setError(null);
      return settings;
    } catch (err) {
      console.error('Failed to fetch brand settings:', err);
      setError(err);
      // Use defaults on error
      setBrandSettings(DEFAULT_BRAND_SETTINGS);
      return DEFAULT_BRAND_SETTINGS;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    brandSettings,
    loading,
    error,
    refetch: () => fetchSettings(true)
  };
};

/**
 * Force clear the brand settings cache
 * Call this after updating settings in Admin
 */
export const clearBrandSettingsCache = () => {
  cachedSettings = null;
  cacheTimestamp = 0;
};

export default useBrandSettings;
